/**
 * Events Management Page
 * إدارة الفعاليات - للخدمات من نوع "فعاليات"
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Calendar, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, X, Check,
  AlertTriangle, Info, Search, Clock, MapPin, Users, Globe,
  Building, Video, RefreshCw, ChevronDown, DollarSign, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/catalog';

const COLORS = {
  primary: '#EF4444',
  primaryLight: '#FEE2E2',
  secondary: '#1B3A6B',
  secondaryLight: '#EEF2F7',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  info: '#2563EB',
};

const EVENT_TYPES = [
  { value: 'workshop', label: 'ورشة عمل' },
  { value: 'seminar', label: 'ندوة' },
  { value: 'conference', label: 'مؤتمر' },
  { value: 'networking', label: 'تواصل وشبكات' },
  { value: 'exhibition', label: 'معرض' },
  { value: 'other', label: 'أخرى' },
];

const DELIVERY_MODES = [
  { value: 'in_person', label: 'حضوري', icon: Building },
  { value: 'online', label: 'أونلاين', icon: Video },
  { value: 'hybrid', label: 'هجين', icon: Globe },
];

function getAuthHeaders() {
  const token = localStorage.getItem('hala_admin_token');
  return { Authorization: `Bearer ${token}` };
}

function Alert({ type = 'info', children, style = {} }) {
  const configs = {
    info: { bg: '#EFF6FF', border: '#3B82F6', color: '#1D4ED8', Icon: Info },
    warning: { bg: '#FFFBEB', border: '#F59E0B', color: '#B45309', Icon: AlertTriangle },
    error: { bg: '#FEF2F2', border: '#EF4444', color: '#B91C1C', Icon: AlertTriangle },
    success: { bg: '#F0FDF4', border: '#22C55E', color: '#15803D', Icon: Check },
  };
  const config = configs[type];
  return (
    <div style={{
      padding: '14px 18px', borderRadius: '10px', backgroundColor: config.bg,
      borderRight: `4px solid ${config.border}`, display: 'flex', alignItems: 'flex-start',
      gap: '12px', ...style
    }}>
      <config.Icon size={18} color={config.color} style={{ flexShrink: 0, marginTop: '2px' }} />
      <div style={{ fontSize: '14px', color: config.color, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Button({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, style = {} }) {
  const variants = {
    primary: { bg: COLORS.primary, color: '#fff' },
    secondary: { bg: COLORS.secondary, color: '#fff' },
    outline: { bg: 'transparent', color: COLORS.text, border: COLORS.border },
    ghost: { bg: 'transparent', color: COLORS.textMuted },
    danger: { bg: COLORS.error, color: '#fff' },
    success: { bg: COLORS.success, color: '#fff' },
  };
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px', gap: '6px' },
    md: { padding: '10px 18px', fontSize: '14px', gap: '8px' },
    lg: { padding: '12px 24px', fontSize: '15px', gap: '10px' },
  };
  const v = variants[variant];
  const s = sizes[size];
  
  return (
    <button onClick={onClick} disabled={disabled || loading} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      gap: s.gap, padding: s.padding, fontSize: s.fontSize, fontWeight: 600,
      fontFamily: "'Tajawal', sans-serif", borderRadius: '10px',
      border: v.border ? `1.5px solid ${v.border}` : 'none',
      backgroundColor: v.bg, color: v.color, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, transition: 'all 0.2s', ...style
    }}>
      {loading ? <RefreshCw size={16} className="animate-spin" /> : Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function Input({ value, onChange, placeholder, type = 'text', style = {} }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
      fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
      outline: 'none', ...style
    }} />
  );
}

function Select({ value, onChange, options, placeholder, style = {} }) {
  return (
    <select value={value} onChange={onChange} style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
      fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
      cursor: 'pointer', outline: 'none', ...style
    }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  );
}

function FormField({ label, required, hint, error, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', marginBottom: '8px', fontSize: '14px',
        fontWeight: 600, color: COLORS.text,
      }}>
        {label}
        {required && <span style={{ color: COLORS.error, marginRight: '4px' }}>*</span>}
        {hint && <span style={{ fontWeight: 400, color: COLORS.textMuted, marginRight: '8px', fontSize: '12px' }}>({hint})</span>}
      </label>
      {children}
      {error && <p style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.error }}>{error}</p>}
    </div>
  );
}

function EventCard({ event, onEdit, onToggle, onDelete }) {
  const modeInfo = DELIVERY_MODES.find(m => m.value === event.delivery_mode);
  const ModeIcon = modeInfo?.icon || Globe;
  const typeInfo = EVENT_TYPES.find(t => t.value === event.event_type);
  
  return (
    <div style={{
      padding: '20px', borderRadius: '16px', backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.border}`, opacity: event.is_active ? 1 : 0.6,
    }}>
      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '16px',
          backgroundColor: `${COLORS.primary}15`, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          flexDirection: 'column',
        }}>
          <Calendar size={24} color={COLORS.primary} />
          {event.event_date && (
            <span style={{ fontSize: '10px', color: COLORS.primary, marginTop: '2px' }}>
              {new Date(event.event_date).toLocaleDateString('ar-SA', { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600, color: COLORS.text }}>
              {event.name_ar}
            </h3>
            {typeInfo && (
              <span style={{
                padding: '2px 8px', backgroundColor: `${COLORS.primary}10`, borderRadius: '4px',
                fontSize: '11px', color: COLORS.primary, fontWeight: 500,
              }}>{typeInfo.label}</span>
            )}
            {!event.is_active && (
              <span style={{
                padding: '2px 8px', backgroundColor: '#FEE2E2', borderRadius: '4px',
                fontSize: '11px', color: COLORS.error, fontWeight: 500,
              }}>معطل</span>
            )}
          </div>
          
          {event.short_description && (
            <p style={{ margin: '0 0 10px', fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.5 }}>
              {event.short_description}
            </p>
          )}
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '10px' }}>
            {event.event_date && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Calendar size={14} color={COLORS.textMuted} />
                <span style={{ fontSize: '13px', color: COLORS.text }}>
                  {new Date(event.event_date).toLocaleDateString('ar-SA')}
                </span>
              </div>
            )}
            {event.duration && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Clock size={14} color={COLORS.textMuted} />
                <span style={{ fontSize: '13px', color: COLORS.text }}>{event.duration}</span>
              </div>
            )}
            {event.capacity && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Users size={14} color={COLORS.textMuted} />
                <span style={{ fontSize: '13px', color: COLORS.text }}>{event.capacity} مقعد</span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ModeIcon size={14} color={COLORS.textMuted} />
              <span style={{ fontSize: '13px', color: COLORS.text }}>{modeInfo?.label || 'غير محدد'}</span>
            </div>
          </div>
          
          {event.location && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <MapPin size={14} color={COLORS.textMuted} />
              <span style={{ fontSize: '13px', color: COLORS.textMuted }}>{event.location}</span>
            </div>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {event.is_free ? (
              <span style={{
                padding: '4px 10px', backgroundColor: '#D1FAE5', borderRadius: '6px',
                fontSize: '12px', color: COLORS.success, fontWeight: 500,
              }}>مجاني</span>
            ) : event.price && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
                <DollarSign size={14} /> {event.price} ر.س
              </span>
            )}
            {event.booking_link && (
              <a href={event.booking_link} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: COLORS.info }}>
                <ExternalLink size={14} /> رابط التسجيل
              </a>
            )}
          </div>
          
          {event.service_name_ar && (
            <div style={{ marginTop: '10px', padding: '8px 12px', backgroundColor: COLORS.bg, borderRadius: '8px' }}>
              <span style={{ fontSize: '12px', color: COLORS.textMuted }}>الخدمة: </span>
              <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text }}>{event.service_name_ar}</span>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <button onClick={() => onToggle(event)} title={event.is_active ? 'تعطيل' : 'تفعيل'}
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent',
              color: event.is_active ? COLORS.success : COLORS.textMuted, cursor: 'pointer' }}>
            {event.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
          </button>
          <button onClick={() => onEdit(event)} title="تعديل"
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: COLORS.secondaryLight,
              color: COLORS.secondary, cursor: 'pointer' }}>
            <Edit2 size={16} />
          </button>
          <button onClick={() => onDelete(event)} title="حذف"
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: '#FEE2E2',
              color: COLORS.error, cursor: 'pointer' }}>
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function EventModal({ isOpen, onClose, onSave, services, cities, editingItem }) {
  const [formData, setFormData] = useState({
    service_id: '', name_ar: '', name_en: '', short_description: '', details: '',
    event_type: 'workshop', event_date: '', duration: '', capacity: '',
    delivery_mode: 'in_person', location: '', booking_link: '', price: '', is_free: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const eventServices = services.filter(s => 
    s.category_type === 'events' || s.category_name_ar?.includes('فعالي')
  );
  
  useEffect(() => {
    if (isOpen) {
      if (editingItem) {
        setFormData({
          service_id: editingItem.service_id || '',
          name_ar: editingItem.name_ar || '',
          name_en: editingItem.name_en || '',
          short_description: editingItem.short_description || '',
          details: editingItem.details || '',
          event_type: editingItem.event_type || 'workshop',
          event_date: editingItem.event_date || '',
          duration: editingItem.duration || '',
          capacity: editingItem.capacity || '',
          delivery_mode: editingItem.delivery_mode || 'in_person',
          location: editingItem.location || '',
          booking_link: editingItem.booking_link || '',
          price: editingItem.price || '',
          is_free: editingItem.is_free !== false,
        });
      } else {
        setFormData({
          service_id: '', name_ar: '', name_en: '', short_description: '', details: '',
          event_type: 'workshop', event_date: '', duration: '', capacity: '',
          delivery_mode: 'in_person', location: '', booking_link: '', price: '', is_free: true,
        });
      }
      setError('');
    }
  }, [isOpen, editingItem]);
  
  const validate = () => {
    if (!formData.service_id) return 'يجب اختيار الخدمة';
    if (!formData.name_ar.trim()) return 'اسم الفعالية بالعربية مطلوب';
    return null;
  };
  
  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onSave({
        ...formData,
        resource_type: 'event',
        capacity: parseInt(formData.capacity) || null,
        price: formData.is_free ? null : parseFloat(formData.price) || null,
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'حدث خطأ أثناء الحفظ');
    }
    setLoading(false);
  };
  
  if (!isOpen) return null;
  
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} />
      
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '750px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto',
        backgroundColor: COLORS.white, borderRadius: '16px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, backgroundColor: COLORS.white, zIndex: 1,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
              {editingItem ? 'تعديل الفعالية' : 'إضافة فعالية جديدة'}
            </h2>
          </div>
          <button onClick={onClose} style={{
            width: '36px', height: '36px', borderRadius: '8px', border: 'none',
            backgroundColor: COLORS.bg, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={20} color={COLORS.textMuted} />
          </button>
        </div>
        
        <div style={{ padding: '24px' }}>
          {error && <Alert type="error" style={{ marginBottom: '20px' }}>{error}</Alert>}
          
          {eventServices.length === 0 && (
            <Alert type="warning" style={{ marginBottom: '20px' }}>
              لا توجد خدمات فعاليات في الكتالوج. يرجى إضافة خدمة من نوع "فعاليات" أولاً.
            </Alert>
          )}
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="الخدمة" required>
              <Select value={formData.service_id}
                onChange={e => setFormData({ ...formData, service_id: e.target.value })}
                placeholder="-- اختر الخدمة --"
                options={eventServices.map(s => ({ value: s.service_id, label: s.name_ar }))} />
            </FormField>
            <FormField label="نوع الفعالية">
              <Select value={formData.event_type}
                onChange={e => setFormData({ ...formData, event_type: e.target.value })}
                options={EVENT_TYPES} />
            </FormField>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="اسم الفعالية بالعربية" required>
              <Input value={formData.name_ar} onChange={e => setFormData({ ...formData, name_ar: e.target.value })}
                placeholder="مثال: ملتقى رواد الأعمال" />
            </FormField>
            <FormField label="اسم الفعالية بالإنجليزية">
              <Input value={formData.name_en} onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                placeholder="Example: Entrepreneurs Meetup" />
            </FormField>
          </div>
          
          <FormField label="وصف مختصر">
            <Input value={formData.short_description} onChange={e => setFormData({ ...formData, short_description: e.target.value })}
              placeholder="فعالية لتبادل الخبرات والتواصل بين رواد الأعمال" />
          </FormField>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <FormField label="تاريخ الفعالية">
              <Input type="date" value={formData.event_date} onChange={e => setFormData({ ...formData, event_date: e.target.value })} />
            </FormField>
            <FormField label="المدة">
              <Input value={formData.duration} onChange={e => setFormData({ ...formData, duration: e.target.value })}
                placeholder="مثال: 3 ساعات" />
            </FormField>
            <FormField label="السعة">
              <Input type="number" value={formData.capacity} onChange={e => setFormData({ ...formData, capacity: e.target.value })}
                placeholder="100" />
            </FormField>
          </div>
          
          <FormField label="نمط التقديم">
            <div style={{ display: 'flex', gap: '12px' }}>
              {DELIVERY_MODES.map(mode => {
                const Icon = mode.icon;
                const isSelected = formData.delivery_mode === mode.value;
                return (
                  <button key={mode.value} type="button"
                    onClick={() => setFormData({ ...formData, delivery_mode: mode.value })}
                    style={{
                      flex: 1, padding: '12px', borderRadius: '10px', border: `2px solid ${isSelected ? COLORS.primary : COLORS.border}`,
                      backgroundColor: isSelected ? `${COLORS.primary}10` : COLORS.white, cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                    }}>
                    <Icon size={20} color={isSelected ? COLORS.primary : COLORS.textMuted} />
                    <span style={{ fontSize: '13px', fontWeight: isSelected ? 600 : 400, color: isSelected ? COLORS.primary : COLORS.text }}>
                      {mode.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </FormField>
          
          <FormField label="الموقع">
            <Input value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })}
              placeholder="مثال: فندق الريتز كارلتون - الرياض" />
          </FormField>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="السعر">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={formData.is_free}
                    onChange={e => setFormData({ ...formData, is_free: e.target.checked, price: e.target.checked ? '' : formData.price })} />
                  مجاني
                </label>
                {!formData.is_free && (
                  <Input type="number" value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })}
                    placeholder="السعر بالريال" style={{ width: '150px' }} />
                )}
              </div>
            </FormField>
            <FormField label="رابط التسجيل">
              <Input value={formData.booking_link} onChange={e => setFormData({ ...formData, booking_link: e.target.value })}
                placeholder="https://..." />
            </FormField>
          </div>
        </div>
        
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: COLORS.bg,
          position: 'sticky', bottom: 0,
        }}>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button variant="success" icon={Check} onClick={handleSubmit} loading={loading}>
            {editingItem ? 'حفظ التعديلات' : 'إضافة الفعالية'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterService, setFilterService] = useState('');
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resRes, svcRes, citiesRes] = await Promise.all([
        axios.get(`${API}/resources`, { params: { resource_type: 'event' }, headers: getAuthHeaders() }),
        axios.get(`${API}/services`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cities`, { headers: getAuthHeaders() }),
      ]);
      
      setEvents(resRes.data || []);
      const svcsData = svcRes.data.items || svcRes.data;
      setServices(Array.isArray(svcsData) ? svcsData : []);
      setCities(citiesRes.data || []);
    } catch (e) {
      console.error('Failed to fetch:', e);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => { fetchData(); }, [fetchData]);
  
  const handleSave = async (data) => {
    if (editingItem) {
      await axios.put(`${API}/resources/${editingItem.resource_id}`, data, { headers: getAuthHeaders() });
    } else {
      await axios.post(`${API}/resources`, data, { headers: getAuthHeaders() });
    }
    fetchData();
    setEditingItem(null);
  };
  
  const handleToggle = async (item) => {
    await axios.post(`${API}/resources/${item.resource_id}/toggle`, {}, { headers: getAuthHeaders() });
    fetchData();
  };
  
  const handleDelete = async (item) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${item.name_ar}"؟`)) return;
    await axios.delete(`${API}/resources/${item.resource_id}`, { headers: getAuthHeaders() });
    fetchData();
  };
  
  const filtered = events.filter(e => {
    if (filterService && e.service_id !== filterService) return false;
    if (searchQuery && !e.name_ar?.includes(searchQuery)) return false;
    return true;
  });
  
  const activeCount = events.filter(e => e.is_active).length;
  
  return (
    <div data-testid="events-page" style={{ padding: '24px', backgroundColor: COLORS.bg, minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            backgroundColor: `${COLORS.primary}15`, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Calendar size={24} color={COLORS.primary} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 700, color: COLORS.text }}>الفعاليات</h1>
            <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
              إدارة الفعاليات والورش والمؤتمرات
            </p>
          </div>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'إجمالي الفعاليات', value: events.length, color: COLORS.primary },
          { label: 'فعاليات نشطة', value: activeCount, color: COLORS.success },
          { label: 'فعاليات معطلة', value: events.length - activeCount, color: COLORS.warning },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: '20px', borderRadius: '12px', backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '14px', color: COLORS.textMuted }}>{stat.label}</div>
          </div>
        ))}
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث بالاسم..."
            style={{ paddingRight: '40px' }} />
        </div>
        <Select value={filterService} onChange={e => setFilterService(e.target.value)}
          placeholder="كل الخدمات"
          options={services.filter(s => s.category_type === 'events' || s.category_name_ar?.includes('فعالي'))
            .map(s => ({ value: s.service_id, label: s.name_ar }))}
          style={{ width: '220px' }} />
        <Button data-testid="add-event-btn" variant="primary" icon={Plus} onClick={() => { setEditingItem(null); setShowModal(true); }}>
          إضافة فعالية
        </Button>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p>جاري التحميل...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px', backgroundColor: COLORS.white,
            borderRadius: '16px', border: `1px solid ${COLORS.border}`,
          }}>
            <Calendar size={48} color={COLORS.textMuted} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
              لا توجد فعاليات
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: COLORS.textMuted }}>
              {searchQuery || filterService ? 'لا توجد نتائج تطابق البحث' : 'قم بإضافة فعاليات جديدة'}
            </p>
            <Button variant="primary" icon={Plus} onClick={() => { setEditingItem(null); setShowModal(true); }}>
              إضافة أول فعالية
            </Button>
          </div>
        ) : (
          filtered.map(e => (
            <EventCard key={e.resource_id} event={e}
              onEdit={(item) => { setEditingItem(item); setShowModal(true); }}
              onToggle={handleToggle} onDelete={handleDelete} />
          ))
        )}
      </div>
      
      <EventModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingItem(null); }}
        onSave={handleSave}
        services={services}
        cities={cities}
        editingItem={editingItem}
      />
    </div>
  );
}
